import React, { useMemo, useState } from "react";
import { Select } from "./Select";
import { buildSubmission } from "./FormSubmission";

const isEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

function FormNotice({ status, message }) {
  if (!status) return null;
  return (
    <div
      className={`form-notice ${status === "success" ? "is-success" : "is-error"}`}
      role={status === "error" ? "alert" : "status"}
      aria-live={status === "error" ? "assertive" : "polite"}
      style={{ marginTop: "2rem", color: "#EA0029" }}
    >
      {message}
    </div>
  );
}

export const ConsultationForm = ({ onSubmitData }) => {
  const [projectType, setProjectType] = useState("");
  const [foundUs, setFoundUs] = useState("");
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [projectDetails, setProjectDetails] = useState("");
  const [touched, setTouched] = useState({});
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const pickValue = (arg) => (arg && typeof arg === "object" ? arg.value : arg) ?? "";
  const requiredKeys = ["name", "company", "email", "projectDetails"];

  const submission = useMemo(
    () =>
      buildSubmission({
        kind: "consultation",
        fields: { projectType, foundUs, name, company, email, phone, projectDetails },
        meta: { page: window.location.pathname },
      }),
    [projectType, foundUs, name, company, email, phone, projectDetails]
  );

  const baseValidation = submission.validate(requiredKeys);
  const emailOk = email ? isEmail(email) : false;

  const errors = {
    name: baseValidation.missing.includes("name") ? "Enter your name." : "",
    company: baseValidation.missing.includes("company") ? "Enter your company name." : "",
    email: baseValidation.missing.includes("email")
      ? "Enter your email address."
      : !emailOk
      ? "Enter a valid email address."
      : "",
    projectDetails: baseValidation.missing.includes("projectDetails") ? "Tell us about the project." : "",
  };

  const canSubmit = status.state !== "loading" && baseValidation.ok && emailOk;
  const showInvalidEmailNotice = !!email && !emailOk;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, company: true, email: true, projectDetails: true });

    if (!canSubmit) {
      setStatus({ state: "error", message: "Please complete the required fields before submitting." });
      return;
    }

    try {
      setStatus({ state: "loading", message: "" });
      if (typeof onSubmitData === "function") await onSubmitData(submission);

      setStatus({ state: "success", message: "Message sent successfully." });
      setTimeout(() => setStatus({ state: "idle", message: "" }), 2000);

      setProjectType("");
      setFoundUs("");
      setName("");
      setCompany("");
      setEmail("");
      setPhone("");
      setProjectDetails("");
      setTouched({});
    } catch (err) {
      setStatus({ state: "error", message: err?.message || "Something went wrong. Please try again." });
    }
  };

  const fieldClass = (key) => (touched[key] && errors[key] ? "has-error" : "");

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="slope-contact-form">
        <div className="select-wrapper">
          <div>
            <label htmlFor="consultation-project-type">Select project type</label>
            <span className="source-select">
              <Select
                id="consultation-project-type"
                name="projectType"
                options={[
                  { value: "", label: "Select an option" },
                  { value: "any", label: "Any" },
                  { value: "virtual-reality", label: "Virtual Reality" },
                  { value: "augmented-reality", label: "Augmented Reality" },
                  { value: "app-software", label: "App & Software" },
                  { value: "installation", label: "Installation" },
                  { value: "simulation", label: "Simulation" },
                ]}
                value={projectType}
                onChange={(arg) => setProjectType(pickValue(arg))}
              />
            </span>
          </div>

          <div>
            <label htmlFor="consultation-found-us">How did you find us?</label>
            <span className="source-select">
              <Select
                id="consultation-found-us"
                name="foundUs"
                options={[
                  { value: "", label: "Select an option" },
                  { value: "google", label: "Google" },
                  { value: "facebook", label: "Facebook" },
                  { value: "other", label: "Other" },
                ]}
                value={foundUs}
                onChange={(arg) => setFoundUs(pickValue(arg))}
              />
            </span>
          </div>
        </div>

        <div className="fields-wrapper">
          <div className="input-contact">
            <span className={`name-field ${fieldClass("name")}`}>
              <label className="screen-reader-text" htmlFor="consultation-name">Your name</label>
              <input
                id="consultation-name"
                name="name"
                value={name}
                onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                onChange={(e) => setName(e.target.value)}
                type="text"
                placeholder="Your name *"
                autoComplete="name"
                required
                aria-invalid={!!(touched.name && errors.name)}
              />
            </span>

            <span className={`company-field ${fieldClass("company")}`}>
              <label className="screen-reader-text" htmlFor="consultation-company">Your company</label>
              <input
                id="consultation-company"
                name="company"
                value={company}
                onBlur={() => setTouched((t) => ({ ...t, company: true }))}
                onChange={(e) => setCompany(e.target.value)}
                type="text"
                placeholder="Your company *"
                autoComplete="organization"
                required
                aria-invalid={!!(touched.company && errors.company)}
              />
            </span>

            <span className={`email-field ${fieldClass("email")}`}>
              <label className="screen-reader-text" htmlFor="consultation-email">Email address</label>
              <input
                id="consultation-email"
                name="email"
                value={email}
                onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="Email address *"
                autoComplete="email"
                required
                aria-invalid={!!(touched.email && errors.email)}
              />
            </span>

            <span className="number-field">
              <label className="screen-reader-text" htmlFor="consultation-phone">Phone number</label>
              <input
                id="consultation-phone"
                name="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                type="tel"
                placeholder="Phone"
                autoComplete="tel"
              />
            </span>
          </div>

          <div className="textarea-contact">
            <span className={`message-field ${fieldClass("projectDetails")}`}>
              <label className="screen-reader-text" htmlFor="consultation-project-details">Tell us about the project</label>
              <textarea
                id="consultation-project-details"
                name="projectDetails"
                value={projectDetails}
                onBlur={() => setTouched((t) => ({ ...t, projectDetails: true }))}
                onChange={(e) => setProjectDetails(e.target.value)}
                placeholder="Tell us about the project *"
                required
                aria-invalid={!!(touched.projectDetails && errors.projectDetails)}
              />
            </span>
          </div>
        </div>

        <div className="submit-contact">
          <input
            type="submit"
            value={status.state === "loading" ? "Sending..." : status.state === "success" ? "Sent" : "Submit"}
            disabled={!canSubmit}
            style={{ cursor: canSubmit ? "pointer" : "not-allowed" }}
          />
          {showInvalidEmailNotice ? (
            <span id="consultation-email-invalid" style={{ marginLeft: "1rem", color: "#EA0029" }}>Email is invalid.</span>
          ) : null}
          <FormNotice
            status={status.state === "success" ? "success" : status.state === "error" ? "error" : ""}
            message={status.message}
          />
        </div>
      </div>
    </form>
  );
};
